
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Button from '../../components/Button'
import { Eye, EyeOff } from 'lucide-react'

// Simple styled components for the form
const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: space-between;
  height: 100%;
  width: 100%;
`
const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  grid-column: ${props => props.fullWidth ? 'span 2' : 'span 1'};
`
const Label = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
`

const RequiredMark = styled.span`
  color: #b3261e;
  margin-left: 2px;
  font-weight: 700;
`
const Input = styled.input`
  padding: 8px;
  padding-right: ${props => (props.$withToggle ? '42px' : '8px')};
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
`

const Select = styled.select`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  width: 100%;
  box-sizing: border-box;
`

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  height: 50%;
  overflow-y: scroll;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }


`

const PasswordFieldWrapper = styled.div`
  position: relative;
`

const PasswordToggleButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4a4a4a;
  padding: 0;
`

const ErrorMessage = styled.div`
  margin-top: 4px;
  color: #b3261e;
  font-size: 0.85rem;
  font-weight: 500;
`

const FieldFeedback = styled.div`
  margin-top: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  color: ${props => props.$type === 'error' ? '#b3261e' : props.$type === 'warning' ? '#e65100' : '#1976d2'};
`

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
`

const RequiredLegend = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
`

const RequiredLegendMark = styled.span`
  color: #b3261e;
  font-weight: 700;
`

const ActionsWrapper = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  return false
}

/**
 * GenericForm maneja el estado de los formularios de administración.
 *
 * Contrato para consumir sugerencias asistidas (ej. asíncronas):
 * - `externalUpdates` (object): Permite inyectar actualizaciones de campos desde la vista
 *   sin perder el estado manual del formulario (ej. { username: 'sugerido123' }).
 * - `asyncFeedback` (object): Muestra mensajes de estado debajo de los inputs.
 *   Formato: { [fieldKey]: { type: 'info'|'warning'|'error', message: 'Texto' } }.
 */
export const GenericForm = ({
  fields,
  initialData,
  onSubmit,
  onCancel,
  title,
  onFieldChange,
  createTitle,
  editTitle,
  externalUpdates,
  asyncFeedback = {}
}) => {
  const [formData, setFormData] = useState({})
  const [showPasswordByField, setShowPasswordByField] = useState({})
  const [validationError, setValidationError] = useState('')

  useEffect(() => {
    if (externalUpdates) {
      setFormData(prev => {
        let hasChanges = false
        const nextData = { ...prev }
        Object.keys(externalUpdates).forEach(key => {
          if (nextData[key] !== externalUpdates[key]) {
            nextData[key] = externalUpdates[key]
            hasChanges = true
          }
        })
        return hasChanges ? nextData : prev
      })
    }
  }, [externalUpdates])

  const missingRequiredField = fields.find(field => field.required && isEmptyValue(formData[field.key]))
  const mismatchedField = fields.find(field =>
    field.matchesField &&
    (formData[field.key] || '') !== (formData[field.matchesField] || '')
  )
  const hasRequiredFields = fields.some(field => field.required)
  const isSubmitDisabled = Boolean(missingRequiredField || mismatchedField)
  const liveMismatchError = mismatchedField &&
    !isEmptyValue(formData[mismatchedField.key]) &&
    !isEmptyValue(formData[mismatchedField.matchesField])
    ? (mismatchedField.errorMessage || `${mismatchedField.label} no coincide.`)
    : ''
  const displayedError = validationError || liveMismatchError

  useEffect(() => {
    if (initialData) {
      const filteredData = {}
      fields.forEach(f => {
        filteredData[f.key] = initialData[f.key] ?? ''
      })
      setFormData(filteredData)
    } else {
      const empty = {}
      fields.forEach(f => {
        empty[f.key] = ''
      })
      setFormData(empty)
    }
    setShowPasswordByField({})
    setValidationError('')
  }, [initialData]) // Removed fields dependency to prevent reset when options update

  const handleChange = (e) => {
    const { name, value } = e.target
    const baseUpdatedData = {
      ...formData,
      [name]: value
    }
    let nextData = baseUpdatedData

    if (onFieldChange) {
      const externalUpdatedData = onFieldChange(name, value, baseUpdatedData)
      if (externalUpdatedData && typeof externalUpdatedData === 'object') {
        nextData = externalUpdatedData
      }
    }

    setFormData(nextData)
    if (validationError) setValidationError('')
  }

  const validateForm = () => {
    if (mismatchedField) {
      setValidationError(mismatchedField.errorMessage || `${mismatchedField.label} no coincide.`)
      return false
    }

    return true
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (missingRequiredField) {
      setValidationError('Completá los campos obligatorios.')
      return
    }

    if (!validateForm()) return

    const submitData = fields.reduce((acc, field) => {
      if (!field.clientOnly) {
        acc[field.key] = formData[field.key]
      }
      return acc
    }, {})

    onSubmit(submitData)
  }

  const togglePasswordVisibility = (fieldKey) => {
    setShowPasswordByField(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }))
  }

  const headerTitle = initialData
    ? (editTitle || `Editar ${title}`)
    : (createTitle || `Crear ${title}`)

  const renderInputField = (field) => {
    const isPassword = field.type === 'password'
    const inputType = isPassword
      ? (showPasswordByField[field.key] ? 'text' : 'password')
      : (field.type || 'text')

    const inputElement = (
      <Input
        name={field.key}
        value={formData[field.key] || ''}
        onChange={handleChange}
        type={inputType}
        required={field.required}
        autoComplete={isPassword ? 'new-password' : 'off'}
        $withToggle={isPassword}
      />
    )

    if (!isPassword) return inputElement

    return (
      <PasswordFieldWrapper>
        {inputElement}
        <PasswordToggleButton
          type='button'
          onClick={() => togglePasswordVisibility(field.key)}
          aria-label={showPasswordByField[field.key] ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          title={showPasswordByField[field.key] ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        >
          {showPasswordByField[field.key]
            ? <EyeOff size={18} />
            : <Eye size={18} />}
        </PasswordToggleButton>
      </PasswordFieldWrapper>
    )
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      <h2>{headerTitle}</h2>
      <FieldsGrid>
        {fields.map(field => (
          <InputGroup key={field.key} fullWidth={field.fullWidth}>
            <Label>
              {field.label}
              {field.required && <RequiredMark>*</RequiredMark>}
            </Label>
            {field.type === 'select'
              ? (
                <Select
                  name={field.key}
                  value={formData[field.key] || ''}
                  onChange={handleChange}
                  required={field.required}
                >
                  <option value=''>Seleccionar...</option>
                  {field.options?.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
                )
              : (
                  renderInputField(field)
                )}
            {asyncFeedback[field.key] && (
              <FieldFeedback $type={asyncFeedback[field.key].type}>
                {asyncFeedback[field.key].message}
              </FieldFeedback>
            )}
          </InputGroup>
        ))}
      </FieldsGrid>
      {displayedError && <ErrorMessage>{displayedError}</ErrorMessage>}
      <FormFooter>
        {hasRequiredFields && (
          <RequiredLegend>
            <RequiredLegendMark>*</RequiredLegendMark> campos obligatorios
          </RequiredLegend>
        )}
        <ActionsWrapper>
          <Button type='button' onClick={onCancel} style={{ background: '#ccc' }}>Cancelar</Button>
          <Button type='submit' color='primary' disabled={isSubmitDisabled}>Guardar</Button>
        </ActionsWrapper>
      </FormFooter>
    </FormContainer>
  )
}
